import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
   Target,  AlertCircle, HeartHandshake,
   ChevronUp, Search, PlusCircle, 
   Flag, Sparkles, Star,
   Loader2, Globe, CheckCircle2, GraduationCap, ChevronRight, Github
} from 'lucide-react';
import axios from 'axios';
import HeroSection from '../components/HeroSection';

const CampaignList = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    category: '',
    search: '',
    sortBy: 'latest'
  });

  // Calculate stats after campaigns are loaded
  const stats = [
    {
      title: "Total Target",
      value: campaigns.reduce((sum, camp) => sum + (camp.financials.targetAmount || 0), 0),
      icon: Target,
      gradientFrom: "from-blue-50",
      gradientTo: "to-blue-100",
      iconColor: "text-blue-600"
    },
    {
      title: "Active Campaigns",
      value: campaigns.length,
      icon: Flag,
      gradientFrom: "from-green-50",
      gradientTo: "to-green-100",
      iconColor: "text-green-600"
    },
    {
      title: "Total Milestones",
      value: campaigns.reduce((sum, camp) => sum + (camp.timeline.milestones?.length || 0), 0),
      icon: CheckCircle2,
      gradientFrom: "from-purple-50",
      gradientTo: "to-purple-100",
      iconColor: "text-purple-600"
    },
    {
      title: "Colleges",
      value: new Set(campaigns.map(camp => camp?.college?.name || "KSV")).size,
      icon: GraduationCap,
      gradientFrom: "from-orange-50",
      gradientTo: "to-orange-100",
      iconColor: "text-orange-600"
    }
  ];

  // Fetch campaigns on component mount
  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        setLoading(true);
        setError('');
        
        // Get token from localStorage
        const token = localStorage.getItem('token');
        
        // Make API call with authorization header
        const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/api/campaigns/approved-campaigns`, {

          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        // Update campaigns state with response data
        setCampaigns(response.data);

      } catch (err) {
        console.error('Error fetching campaigns:', err);
        setError(err.response?.data?.message || 'Error fetching campaigns');
      } finally {
        setLoading(false);
      }
    };

    fetchCampaigns();
  }, []);

  // Filter campaigns based on search and category
  const filteredCampaigns = campaigns.filter(campaign => {
    const matchesSearch = campaign.title.toLowerCase().includes(filters.search.toLowerCase()) ||
                         campaign.description.short.toLowerCase().includes(filters.search.toLowerCase());
    const matchesCategory = !filters.category || campaign.category === filters.category;
    return matchesSearch && matchesCategory;
  });

  // Sort campaigns based on selected sort option
  const sortedCampaigns = [...filteredCampaigns].sort((a, b) => {
    switch (filters.sortBy) {
      case 'mostFunded':
        return (b.financials.raisedAmount || 0) - (a.financials.raisedAmount || 0);
      case 'endingSoon':
        return new Date(a.timeline.endDate) - new Date(b.timeline.endDate);
      case 'latest':
      default:
        return new Date(b.createdAt) - new Date(a.createdAt);
    }
  });

  // Campaign Card Component
  const CampaignCard = ({ campaign }) => {
    const progress = ((campaign.financials.raisedAmount || 0) / campaign.financials.targetAmount) * 100;
    const daysLeft = Math.ceil((new Date(campaign.timeline.endDate) - new Date()) / (1000 * 60 * 60 * 24));
    
    // Find the primary image or take the first image
    const coverImage = campaign.media?.images?.find(img => img.isPrimary)?.url || 
                      campaign.media?.images?.[0]?.url ||
                      'https://via.placeholder.com/400x200';
    
    return (
       <div className="bg-white rounded-xl shadow-sm hover:shadow-xl transition-all duration-300">
      {/* Updated Card Header with actual image */}
      <div className="relative h-48 rounded-t-xl overflow-hidden">
        <img 
          src={coverImage}
          alt={campaign.title}
          className="w-full h-full object-cover"
        />
        {/* Overlay gradient for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        
        {/* Category Badge */}
        <span className="absolute top-4 left-4 px-3 py-1 bg-white/90 rounded-full text-sm font-medium text-gray-700 flex items-center gap-1">
          <Star className="w-3 h-3" />
          {campaign.category}
        </span>

          {/* Organizer Info */}
          <div className="absolute bottom-4 left-4 right-4 flex items-center space-x-3">
            <div className="w-10 h-10 rounded-full border-2 border-white overflow-hidden">
              <img 
                src={campaign?.organizer?.organizedBy?.profileImage || campaign?.organizer?.organizedBy?.personalInfo?.avatar} 
                alt={campaign.organizer.organizedBy.firstName}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.src = `https://ui-avatars.com/api/?name=${campaign.organizer.organizedBy.firstName}+${campaign.organizer.organizedBy.lastName}`
                }}
              />
            </div>
            <div className="text-white">
              <p className="font-medium">
                {campaign.organizer.organizedBy.firstName} {campaign.organizer.organizedBy.lastName}
              </p>
              <p className="text-sm opacity-75">{campaign?.college?.name || "KSV"}</p>
            </div>
          </div>
        </div>

        {/* Card Content */}
        <div className="p-6">
          <h3 className="text-xl font-semibold mb-2 text-gray-900 group-hover:text-blue-600 transition-colors">
            {campaign.title}
          </h3>
          <p className="text-gray-600 text-sm mb-4 line-clamp-2">
            {campaign.description.short}
          </p>

          {/* Progress Section */}
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="font-medium">₹{campaign.financials.raisedAmount?.toLocaleString() || 0}</span>
              <span className="text-gray-600">of ₹{campaign.financials.targetAmount?.toLocaleString()}</span>
            </div>
            {/* Custom Progress Bar */}
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div 
                className="h-full bg-blue-600 rounded-full transition-all duration-500"
                style={{ width: `${Math.min(progress, 100)}%` }}
              />
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-3 gap-2 pt-2">
              <div className="text-center p-2 bg-gray-50 rounded-lg">
                <p className="text-lg font-semibold text-blue-600">{progress.toFixed(1)}%</p>
                <p className="text-xs text-gray-600">Funded</p>
              </div>
              <div className="text-center p-2 bg-gray-50 rounded-lg">
                <p className="text-lg font-semibold text-green-600">{daysLeft}</p>
                <p className="text-xs text-gray-600">Days Left</p>
              </div>
              <div className="text-center p-2 bg-gray-50 rounded-lg">
                <p className="text-lg font-semibold text-purple-600">
                  {campaign.timeline.milestones?.length || 0}
                </p>
                <p className="text-xs text-gray-600">Milestones</p>
              </div>
            </div>
          </div>

          {/* Tech Stack */}
          {campaign.technicalDetails.stack.length > 0 && (
            <div className="mt-4">
              <p className="text-sm font-medium text-gray-700 mb-2">Tech Stack</p>
              <div className="flex flex-wrap gap-2">
                {campaign.technicalDetails.stack.map((tech, index) => (
                  <span key={index} className="px-2 py-1 bg-gray-100 text-gray-700 rounded-md text-xs">
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Card Footer */}
        <div className="px-6 py-4 bg-gray-50 rounded-b-xl flex justify-between items-center">
          <div className="flex items-center space-x-4">
            {campaign.projectLinks.github && (
              <a 
                href={campaign.projectLinks.github} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-gray-900 transition-colors"
              >
                <Github className="h-5 w-5" />
              </a>
            )}
            {campaign.projectLinks.demo && (
              <a 
                href={campaign.projectLinks.demo} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-gray-900 transition-colors"
              >
                <Globe className="h-5 w-5" />
              </a>
            )}
          </div>
          <button 
            onClick={() => navigate(`/campaigns/${campaign._id}`)}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg
              hover:bg-blue-700 transition-colors text-sm"
          >
            View Details
            <ChevronRight className="h-4 w-4 ml-1" />
          </button>
        </div>
      </div>
    );
  };

  // Loading State
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
          <p className="text-gray-600 animate-pulse">Loading campaigns...</p>
        </div>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-red-50 text-red-700 p-4 rounded-lg flex items-center">
          <AlertCircle className="h-5 w-5 mr-2" />
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <HeroSection
  title="Empowering Innovation Through Crowdfunding"
  subtitle="Support groundbreaking projects and help turn innovative ideas into reality"
  showSearch={true}
  searchConfig={{
    value: filters.search,
    onChange: (e) => setFilters({ ...filters, search: e.target.value }),
    placeholder: "Search campaigns by title, type, or description..."
  }}
  statsConfig={[
    { value: stats[0].value.toLocaleString(), label: "Total Target" },
    { value: stats[1].value.toLocaleString(), label: "Active Campaigns" },
    { value: stats[2].value.toLocaleString(), label: "Total Milestones" },
    { value: stats[3].value.toLocaleString(), label: "Colleges" }
  ]}
/>

      {/* Stats Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat, index) => (
            <div
              key={index}
              className={`bg-gradient-to-br ${stat.gradientFrom} ${stat.gradientTo} 
                rounded-xl shadow-sm p-6`}
            >
              <div className="flex items-center space-x-4">
                <div className="p-3 rounded-xl bg-white">
                  <stat.icon className={`h-6 w-6 ${stat.iconColor}`} />
                </div>
                <div>
                  <p className="text-sm text-gray-600">{stat.title}</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {typeof stat.value === 'number' ? stat.value.toLocaleString() : stat.value}
                    </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Main Content - Campaigns Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Sort Controls */}
        <div className="mb-8 flex items-center justify-between">
          <h2 className="text-2xl font-semibold text-gray-900">
            {sortedCampaigns.length} Active Campaigns
          </h2>
          <div className="flex items-center gap-4">
            <select
              value={filters.sortBy}
              onChange={(e) => setFilters(prev => ({ ...prev, sortBy: e.target.value }))}
              className="px-4 py-2 bg-white rounded-lg border border-gray-200 text-gray-700 
                focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="latest">Latest First</option>
              <option value="mostFunded">Most Funded</option>
              <option value="endingSoon">Ending Soon</option>
            </select>
            <button
              onClick={() => navigate('/campaigns/create')}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg
                hover:bg-blue-700 transition-colors"
            >
              <PlusCircle className="h-5 w-5 mr-2" />
              Start Campaign
            </button>
          </div>
        </div>

        {/* Campaigns Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sortedCampaigns.map((campaign) => (
            <CampaignCard key={campaign._id} campaign={campaign} />
          ))}
        </div>

        {/* Empty State */}
        {sortedCampaigns.length === 0 && !loading && (
          <div className="text-center py-16 bg-white rounded-xl shadow-sm">
            <div className="mb-4 p-4 rounded-full bg-blue-50 w-16 h-16 mx-auto flex items-center justify-center">
              <HeartHandshake className="h-8 w-8 text-blue-500" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No campaigns found</h3>
            <p className="text-gray-600 mb-8">
              {filters.search || filters.category ? 
                'Try adjusting your search or filters' : 
                'Be the first to start a fundraising campaign'
              }
            </p>
            <button
              onClick={() => navigate('/campaigns/create')}
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg
                hover:bg-blue-700 transition-colors"
            >
              <Sparkles className="h-5 w-5 mr-2" />
              Create Campaign
            </button>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="fixed bottom-4 right-4 bg-red-50 text-red-700 px-4 py-3 rounded-lg 
            shadow-lg flex items-center"
          >
            <AlertCircle className="h-5 w-5 mr-2" />
            <span>{error}</span>
            <button 
              onClick={() => setError('')}
              className="ml-4 text-red-500 hover:text-red-700"
            >
              <p className="h-5 w-5" >X</p>
            </button>
          </div>
        )}
      </div>

      {/* Optional: Scroll to Top Button */}
      {typeof window !== 'undefined' && window.pageYOffset > 300 && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-4 right-4 p-3 bg-white rounded-full shadow-lg hover:shadow-xl
            transition-all duration-300"
        >
          <ChevronUp className="h-6 w-6 text-gray-600" />
        </button>
      )}
    </div>
  );
};

export default CampaignList;